// Report It submission flow (v7 §6.1, MV-1077). Ties the client-snapshot
// capture to the API client: capture → assemble → submit. Dependency-injected
// (capture / submit / element accessors / entity provider) so the whole flow is
// unit-tested; the React hook that wires the live GameCast context is then a
// thin wrapper that supplies the real dependencies.

import {
  ReportItClientSnapshot,
} from './snapshotTypes';
import { CaptureReportItSnapshotOptions } from './captureSnapshot';
import {
  SubmitReportInput,
  SubmitReportResponse,
  ReportCorrelationEntities,
  ReportAttachmentFile,
  ReportAttachmentState,
  ReportIncidentType,
  ReportSymptomHint,
  ReportSeverityHint,
  PresignedAttachmentUpload,
  CompletedPartClaim,
  collectBrowserReportContext,
  buildAttachmentDeclarations,
  uploadAttachmentParts,
  completeAttachmentUpload,
  retryAttachmentUpload,
} from './reportItApi';

/** Coarse phase of the submission, surfaced to the UI for status text. */
export type ReportSubmissionPhase = 'capturing' | 'submitting' | 'uploading';

/** The user- + session-supplied fields of a report. The flow fills in the
 *  captured snapshot, browser context, and correlation entities. */
export interface ReportDraft {
  environment: string;
  eventId?: string;
  /** Idempotency inputs (v7 §10.3) — generated once when the dialog opens. */
  browserSessionId: string;
  clientReportNonce: string;
  incidentType: ReportIncidentType;
  occurredAt: string;
  description?: string;
  symptomHints?: ReportSymptomHint[];
  severityHint?: ReportSeverityHint;
  attachDiagnostics?: boolean;
  attachments?: ReportAttachmentFile[];
  /** Build versions surfaced in the report's entity panel (MV-1396). */
  hubVersion?: string;
  spatialManagerVersion?: string;
}

export interface AssembleReportArgs {
  draft: ReportDraft;
  snapshot?: ReportItClientSnapshot;
  entities?: ReportCorrelationEntities;
  browserContext?: {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  };
  /** Scrubbed browser console tail captured at submit time (MV-1396). */
  browserLogs?: string;
}

/**
 * Merge a draft + captured snapshot + browser context + correlation entities
 * into the final SubmitReportInput (pure when `browserContext` is supplied;
 * otherwise reads browser globals via collectBrowserReportContext).
 */
export function assembleReportInput(args: AssembleReportArgs): SubmitReportInput {
  const ctx = args.browserContext ?? collectBrowserReportContext();

  return {
    environment: args.draft.environment,
    eventId: args.draft.eventId,
    browserSessionId: args.draft.browserSessionId,
    clientReportNonce: args.draft.clientReportNonce,
    incidentType: args.draft.incidentType,
    occurredAt: args.draft.occurredAt,
    description: args.draft.description,
    symptomHints: args.draft.symptomHints,
    severityHint: args.draft.severityHint,
    attachDiagnostics: args.draft.attachDiagnostics,
    attachments: buildAttachmentDeclarations(args.draft.attachments),
    userLocalTime: ctx.userLocalTime,
    userTimeZone: ctx.userTimeZone,
    language: ctx.language,
    entities: args.entities,
    clientSnapshot: args.snapshot,
    hubVersion: args.draft.hubVersion,
    spatialManagerVersion: args.draft.spatialManagerVersion,
    browserLogs: args.browserLogs,
  };
}

/** What became of one attachment's bytes, so the dialog can offer a retry. */
export interface AttachmentDeliveryOutcome {
  index: number;
  attachmentId?: string;
  delivered: boolean;
  error?: unknown;
}

/** The upload half of the flow, injectable so the delivery loop is testable. */
export interface AttachmentTransportDeps {
  uploadParts?: (
    file: Blob,
    upload: PresignedAttachmentUpload,
    options?: { onProgress?: (fraction: number) => void },
  ) => Promise<CompletedPartClaim[]>;
  completeUpload?: (
    reportId: string,
    attachmentId: string,
    claim: { uploadAttemptId: string; parts: CompletedPartClaim[] },
  ) => Promise<ReportAttachmentState>;
  retryUpload?: (
    reportId: string,
    attachmentId: string,
  ) => Promise<PresignedAttachmentUpload>;
}

export interface ReportItSubmissionDeps extends AttachmentTransportDeps {
  capture: (
    opts: CaptureReportItSnapshotOptions,
  ) => Promise<ReportItClientSnapshot>;
  submit: (input: SubmitReportInput) => Promise<SubmitReportResponse>;
  getVideoElement: () => HTMLVideoElement | null;
  getAudioElement?: () => HTMLMediaElement | null;
  getStats?: () => Promise<RTCStatsReport>;
  /** Latest stream connection state + transition history (MV-1077). */
  getConnectionState?: CaptureReportItSnapshotOptions['getConnectionState'];
  /** Page-lifetime rolling RTC window, preferred over on-demand sampling (MV-1077). */
  getBufferedRtcSamples?: CaptureReportItSnapshotOptions['getBufferedRtcSamples'];
  /** Scrubbed browser console tail, captured at submit time (MV-1396). */
  getBrowserLogs?: () => string | undefined;
  getEntities?: () => ReportCorrelationEntities;
  browserContext?: () => {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  };
  /** Notified as the flow moves through capture → submit → upload. */
  onPhaseChange?: (phase: ReportSubmissionPhase) => void;
  /** Per-file upload progress (0..1), keyed by declaration index. */
  onAttachmentProgress?: (index: number, fraction: number) => void;
  /** Notified per attachment as its bytes land or fail, so the dialog can keep
   *  a failed file and offer a retry. */
  onAttachmentOutcome?: (outcome: AttachmentDeliveryOutcome) => void;
  /** Notified when attachment upload fails *after* the report was created —
   *  the report itself still succeeded. */
  onAttachmentError?: (error: unknown) => void;
}

/**
 * Push one attachment's bytes up and hand the part claims to the backend, which
 * performs the completion itself. Used by both the initial submission and the
 * per-file retry, so both paths obey the same fixed part plan.
 */
export async function deliverAttachment(args: {
  reportId: string;
  file: Blob;
  upload: PresignedAttachmentUpload;
  deps: AttachmentTransportDeps;
  onProgress?: (fraction: number) => void;
}): Promise<ReportAttachmentState> {
  const uploadParts = args.deps.uploadParts ?? uploadAttachmentParts;
  const complete = args.deps.completeUpload ?? completeAttachmentUpload;

  const parts = await uploadParts(args.file, args.upload, {
    onProgress: args.onProgress,
  });

  return complete(args.reportId, args.upload.attachmentId, {
    uploadAttemptId: args.upload.uploadAttemptId,
    parts,
  });
}

/**
 * Take a fresh attempt for an attachment whose bytes never landed and deliver
 * them again (§8.2). The backend re-authorises the reporter and the retry
 * window, so a rejection here is final for that attachment.
 */
export async function retryAttachmentDelivery(args: {
  reportId: string;
  file: Blob;
  attachmentId: string;
  deps: AttachmentTransportDeps;
  onProgress?: (fraction: number) => void;
}): Promise<ReportAttachmentState> {
  const requestRetry = args.deps.retryUpload ?? retryAttachmentUpload;
  const upload = await requestRetry(args.reportId, args.attachmentId);

  return deliverAttachment({
    reportId: args.reportId,
    file: args.file,
    upload,
    deps: args.deps,
    onProgress: args.onProgress,
  });
}

/**
 * Deliver every declared attachment. One file's failure must not abandon the
 * others, so each outcome is reported individually and the loop continues.
 */
async function deliverAttachments(
  deps: ReportItSubmissionDeps,
  reportId: string,
  files: ReportAttachmentFile[],
  uploads: PresignedAttachmentUpload[],
): Promise<AttachmentDeliveryOutcome[]> {
  const outcomes: AttachmentDeliveryOutcome[] = [];

  for (let index = 0; index < files.length; index += 1) {
    // Declaration order is the manifest order, which is the order the backend
    // signed the attempts in.
    const upload = uploads[index];
    const outcome: AttachmentDeliveryOutcome = {
      index,
      attachmentId: upload?.attachmentId,
      delivered: false,
    };

    if (!upload) {
      outcome.error = new Error(
        `No upload target for attachment ${files[index].filename ?? index + 1}`,
      );
    } else {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deliverAttachment({
          reportId,
          file: files[index].file,
          upload,
          deps,
          onProgress: deps.onAttachmentProgress
            ? (fraction) => deps.onAttachmentProgress?.(index, fraction)
            : undefined,
        });
        outcome.delivered = true;
      } catch (err) {
        outcome.error = err;
      }
    }

    deps.onAttachmentOutcome?.(outcome);
    outcomes.push(outcome);
  }

  return outcomes;
}

/**
 * Run the full submission flow: capture the client snapshot (best-effort —
 * a capture failure must not block the user's report), assemble the payload,
 * and submit. Returns the API response.
 */
export async function runReportItSubmission(
  deps: ReportItSubmissionDeps,
  draft: ReportDraft,
): Promise<SubmitReportResponse> {
  let snapshot: ReportItClientSnapshot | undefined;

  deps.onPhaseChange?.('capturing');

  try {
    snapshot = await deps.capture({
      video: deps.getVideoElement(),
      audio: deps.getAudioElement ? deps.getAudioElement() : undefined,
      getStats: deps.getStats,
      getConnectionState: deps.getConnectionState,
      getBufferedRtcSamples: deps.getBufferedRtcSamples,
    });
  } catch {
    // A diagnostic-capture failure must never stop a user from reporting — the
    // backend playbook degrades to needs_manual_review without a snapshot.
    snapshot = undefined;
  }

  const input = assembleReportInput({
    draft,
    snapshot,
    entities: deps.getEntities ? deps.getEntities() : undefined,
    browserContext: deps.browserContext ? deps.browserContext() : undefined,
    browserLogs: deps.getBrowserLogs ? deps.getBrowserLogs() : undefined,
  });

  deps.onPhaseChange?.('submitting');

  const response = await deps.submit(input);

  const files = draft.attachments ?? [];

  if (files.length === 0) {
    return response;
  }

  deps.onPhaseChange?.('uploading');

  // A duplicate POST returns no part URLs, so there is nothing to upload
  // against — the attachment ids it does return drive the retry route instead.
  const uploads = response.attachmentUploads ?? [];

  const outcomes = await deliverAttachments(deps, response.reportId, files, uploads);
  const failed = outcomes.filter((outcome) => !outcome.delivered);

  if (failed.length > 0) {
    // The report is already created server-side, so a failed attachment upload
    // must not present as a failed report — surface it as a warning and still
    // return the successful report response.
    deps.onAttachmentError?.(failed[0].error);
  }

  return response;
}
